// The save controller behind ONE open draft (docs/superpowers/specs/2026-10-13-multi-ticket-
// drafts-design.md, "Saving, syncing, and failure"). Pure over its injected parts:
//
//   save(body)   → Promise<{ revision }>. Sends `PUT /api/ticket-drafts/:id` and resolves with
//                  the response's `data` (api/client.js returns the whole `{ data }` envelope;
//                  the caller unwraps it). Rejects with api/client.js's `ApiError`
//                  (`status`, `code`, `details`) or whatever `fetch` threw.
//   buffer       → a `createBuffer(storage)` from draft-buffer.js.
//   setTimer / clearTimer  → `(fn, ms) => handle` / `(handle) => void`, so tests drive a fake clock.
//   onState(snapshot)      → called once per change of state. If it throws, the controller
//                  carries on and the error goes to `onCallbackError` (default: an
//                  unhandled rejection, so it is seen, not swallowed). A callback bug is
//                  never reported as a failed save.
//
// `edit(body)` takes the WHOLE current state as a PUT body (`toSaveBody`). The controller
// stamps `base_revision` itself, because only it knows which revision the last save
// returned; whatever the caller put there is overwritten.
//
// One save is in flight at a time. An edit made during a save replaces what is waiting to be
// sent and goes out in the NEXT save, on the revision the in-flight one returned. Every edit
// is written to the buffer before anything is sent, and the buffer entry is acknowledged
// when a save lands.
//
// STATES (the snapshot is `{ state, revision, ...extras }`):
//   idle       nothing unsaved.
//   dirty      an edit is waiting for the debounce (or, after a save, for the next save).
//   saving     a save is in flight; edits meanwhile are waiting behind it.
//   failed     the save did not land. `{ error, transient }`. The edit stays buffered and
//              waiting; the next `edit` or `flush` tries again. `transient` is true for a
//              failure that may pass on its own (see isTransientError), false for a refusal
//              that repeating cannot fix (a 422, a 403, DRAFT_LIMIT, or DRAFT_TOO_LARGE: the
//              body is over 256 KB, which the controller checks itself and never sends).
//   stale      409 DRAFT_STALE: another PC saved first. `{ unsaved }`, `revision` is the
//              server's current one.
//   taken      409 DRAFT_TAKEN_OVER: a lead took the draft. `{ by, unsaved }`.
//   filed      409 DRAFT_FILED: `{ public_ref }`.
//   discarded  409 DRAFT_DISCARDED.
// After stale, taken, filed or discarded the controller saves nothing more and ignores edits.
// `unsaved` is the NEWEST body the agent had (a later edit beats the refused save's), so the
// screen can show their text once. For stale and taken the buffer entry is KEPT: the text
// is still the agent's until the screen has shown it, and the screen then calls
// `buffer.remove`. For filed and discarded it is dropped: the ticket holds the content, or
// the call was abandoned, and a replay would only be refused again.

import { MAX_DRAFT_BYTES, draftBytes } from './draft-model.js';

const DEFAULT_DEBOUNCE_MS = 400;

/**
 * Whether a failed save may succeed if simply tried again: the network was down (`fetch`
 * rejects with a TypeError, which has no `status`), status 0, or a 5xx, including api/
 * client.js's INTERNAL_ERROR for a proxy answering HTML. Anything else, a 4xx, is the
 * server saying no.
 */
export function isTransientError(error) {
  const status = error?.status;
  return typeof status !== 'number' || status === 0 || status >= 500;
}

export function createDraftSync({
  save,
  buffer,
  userId,
  draftId,
  revision,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (handle) => clearTimeout(handle),
  onState = () => {},
  onCallbackError = (error) => { Promise.reject(error); },
}) {
  let current = revision;
  let state = 'idle';
  let extras = {};
  let pending = null; // the newest body not yet acknowledged
  let inFlightBody = null;
  let inFlight = null; // the promise of the save in flight
  let timer = null;
  let terminal = false;
  let disposed = false;

  const snapshot = () => ({ state, revision: current, ...extras });

  function setState(next, nextExtras = {}) {
    const changed = next !== state;
    state = next;
    extras = nextExtras;
    if (changed && !disposed) {
      try {
        onState(snapshot());
      } catch (error) {
        onCallbackError(error);
      }
    }
  }

  function disarm() {
    if (timer !== null) clearTimer(timer);
    timer = null;
  }

  function arm() {
    disarm();
    timer = setTimer(() => {
      timer = null;
      if (pending && !inFlight && !terminal && !disposed) startSave();
    }, debounceMs);
  }

  function close(nextState, nextExtras) {
    terminal = true;
    disarm();
    pending = null;
    setState(nextState, nextExtras);
  }

  function onSaved(result) {
    current = result.revision;
    let rebased = true;
    if (pending) {
      // Typed during the save: still unsaved, and now built on the revision that came back.
      pending = { ...pending, base_revision: current };
      rebased = buffer.write(userId, draftId, { baseRevision: current, body: pending });
    }
    // If the rebased copy could not be stored, the older entry (built on the previous
    // revision, but holding this same text) is now the only copy: leave it, do not ack it away.
    if (rebased) buffer.ack(userId, draftId, current);
    if (pending) {
      setState('dirty');
      if (!disposed) arm();
    } else {
      setState('idle');
    }
  }

  function onRefused(error) {
    const unsaved = pending ?? inFlightBody;
    switch (error?.code) {
      case 'DRAFT_STALE':
        if (Number.isInteger(error.details?.revision)) current = error.details.revision;
        close('stale', { unsaved });
        return;
      case 'DRAFT_TAKEN_OVER':
        close('taken', { by: error.details?.by, unsaved });
        return;
      case 'DRAFT_FILED':
        buffer.remove(userId, draftId);
        close('filed', { public_ref: error.details?.public_ref });
        return;
      case 'DRAFT_DISCARDED':
        buffer.remove(userId, draftId);
        close('discarded');
        return;
      default:
        // Not landed, so it is still unsaved, and the buffer already holds it.
        pending = unsaved;
        setState('failed', { error, transient: isTransientError(error) });
    }
  }

  function startSave() {
    if (draftBytes(pending) > MAX_DRAFT_BYTES) {
      // The server would refuse it for good (422), so do not send it. Treated like any
      // refused save: the edit stays pending and buffered, and the next edit tries again.
      setState('failed', {
        error: {
          code: 'DRAFT_TOO_LARGE',
          message: 'This draft is too large to save (256 KB). Shorten the text to keep saving.',
        },
        transient: false,
      });
      return null;
    }
    const body = { ...pending, base_revision: current };
    inFlightBody = body;
    pending = null;
    setState('saving');
    inFlight = (async () => {
      // `save` is called right away (a test, and a click, see it at once), but a throw from
      // it must still arrive after the `await` below, never before `inFlight` is assigned.
      let sending;
      try {
        sending = save(body);
      } catch (error) {
        sending = Promise.reject(error);
      }
      let result;
      try {
        result = await sending;
      } catch (error) {
        inFlight = null;
        onRefused(error);
        return;
      }
      inFlight = null;
      if (!Number.isInteger(result?.revision)) {
        // Not a save the controller can build the next one on. Treat it as one that did
        // not land: the edit stays pending, and the next try will find out what the server holds.
        onRefused(Object.assign(new Error('The server answered a save without saying which revision it is.'), {
          code: 'DRAFT_BAD_SAVE_RESPONSE',
        }));
        return;
      }
      onSaved(result);
    })();
    return inFlight;
  }

  return {
    /** The whole current state of the screen, as a PUT body (`toSaveBody`). */
    edit(body) {
      if (terminal || disposed) return;
      pending = { ...body, base_revision: current };
      buffer.write(userId, draftId, { baseRevision: current, body: pending });
      // During a save the edit just waits: `onSaved` arms the timer for the next one.
      if (!inFlight) {
        setState('dirty');
        arm();
      }
    },

    /**
     * Saves what is unsaved now, waiting for a save in flight first. Never rejects. Resolves
     * with the snapshot: only `idle` means everything is saved (`failed` means it is not and
     * the edit is still buffered; a terminal state means the draft was closed elsewhere).
     */
    async flush() {
      disarm();
      if (terminal || disposed) return snapshot();
      if (inFlight) await inFlight;
      disarm();
      if (!terminal && !disposed && pending) await (inFlight ?? startSave());
      return snapshot();
    },

    getState: snapshot,

    /** Stops the timer and ignores further edits (the screen is going away). The buffer keeps what it holds. */
    dispose() {
      disposed = true;
      disarm();
    },
  };
}
